// Offline storage service using IndexedDB
// Stores inspections when offline and syncs when back online

const DB_NAME = 'equipr_guardian_offline';
const DB_VERSION = 1;
const STORE_INSPECTIONS = 'offline_inspections';
const STORE_TEMPLATES = 'cached_templates';
const STORE_ASSETS = 'cached_assets';

let db = null;

// Initialize IndexedDB
export const initOfflineDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('Failed to open IndexedDB');
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      // Store for offline inspections
      if (!database.objectStoreNames.contains(STORE_INSPECTIONS)) {
        const inspectionsStore = database.createObjectStore(STORE_INSPECTIONS, { keyPath: 'local_id' });
        inspectionsStore.createIndex('synced', 'synced', { unique: false });
        inspectionsStore.createIndex('created_at', 'created_at', { unique: false });
      }

      // Store for cached templates (for offline inspection)
      if (!database.objectStoreNames.contains(STORE_TEMPLATES)) {
        database.createObjectStore(STORE_TEMPLATES, { keyPath: 'id' });
      }

      // Store for cached assets (for offline inspection)
      if (!database.objectStoreNames.contains(STORE_ASSETS)) {
        database.createObjectStore(STORE_ASSETS, { keyPath: 'id' });
      }
    };
  });
};

// Generate a local ID for offline records
export const generateLocalId = () => {
  return `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Save inspection offline
export const saveOfflineInspection = async (inspection) => {
  const database = await initOfflineDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORE_INSPECTIONS], 'readwrite');
    const store = transaction.objectStore(STORE_INSPECTIONS);

    const record = {
      ...inspection,
      local_id: inspection.local_id || generateLocalId(),
      synced: false,
      offline_created_at: new Date().toISOString(),
    };

    const request = store.put(record);

    request.onsuccess = () => resolve(record);
    request.onerror = () => reject(request.error);
  });
};

// Get all unsynced inspections
export const getUnsyncedInspections = async () => {
  const database = await initOfflineDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORE_INSPECTIONS], 'readonly');
    const store = transaction.objectStore(STORE_INSPECTIONS);
    const index = store.index('synced');
    const request = index.getAll(IDBKeyRange.only(false));

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// Mark inspection as synced
export const markInspectionSynced = async (localId) => {
  const database = await initOfflineDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORE_INSPECTIONS], 'readwrite');
    const store = transaction.objectStore(STORE_INSPECTIONS);
    const getRequest = store.get(localId);

    getRequest.onsuccess = () => {
      const record = getRequest.result;
      if (record) {
        record.synced = true;
        record.synced_at = new Date().toISOString();
        const putRequest = store.put(record);
        putRequest.onsuccess = () => resolve(record);
        putRequest.onerror = () => reject(putRequest.error);
      } else {
        resolve(null);
      }
    };

    getRequest.onerror = () => reject(getRequest.error);
  });
};

// Delete synced inspections (cleanup)
export const clearSyncedInspections = async () => {
  const database = await initOfflineDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORE_INSPECTIONS], 'readwrite');
    const store = transaction.objectStore(STORE_INSPECTIONS);
    const index = store.index('synced');
    const request = index.openCursor(IDBKeyRange.only(true));

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        store.delete(cursor.primaryKey);
        cursor.continue();
      } else {
        resolve();
      }
    };

    request.onerror = () => reject(request.error);
  });
};

// Cache templates for offline use
export const cacheTemplates = async (templates) => {
  const database = await initOfflineDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORE_TEMPLATES], 'readwrite');
    const store = transaction.objectStore(STORE_TEMPLATES);

    // Clear existing and add new
    store.clear();
    templates.forEach(template => store.put(template));

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
};

// Get cached templates
export const getCachedTemplates = async () => {
  const database = await initOfflineDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORE_TEMPLATES], 'readonly');
    const store = transaction.objectStore(STORE_TEMPLATES);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// Cache assets for offline use
export const cacheAssets = async (assets) => {
  const database = await initOfflineDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORE_ASSETS], 'readwrite');
    const store = transaction.objectStore(STORE_ASSETS);

    // Clear existing and add new
    store.clear();
    assets.forEach(asset => store.put(asset));

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
};

// Get cached asset by ID
export const getCachedAsset = async (assetId) => {
  const database = await initOfflineDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORE_ASSETS], 'readonly');
    const store = transaction.objectStore(STORE_ASSETS);
    const request = store.get(assetId);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// Get all cached assets
export const getCachedAssets = async () => {
  const database = await initOfflineDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORE_ASSETS], 'readonly');
    const store = transaction.objectStore(STORE_ASSETS);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// Check if we're online
export const isOnline = () => navigator.onLine;

// Get offline inspection count
export const getOfflineInspectionCount = async () => {
  try {
    const unsynced = await getUnsyncedInspections();
    return unsynced.length;
  } catch {
    return 0;
  }
};
